import { useState, useEffect, useMemo } from 'react';
import { CopyIcon, EditIcon, TrashIcon, RefreshCwIcon } from 'lucide-react';
import { deltaToPlainText } from '@/components/custom/quill-editor';
import { DataTable, type Column } from '@/components/custom/data-table';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Button } from '@/components/ui/button';
import { assetsApi } from '@/lib/api/assets';
import { collectionDataApi } from '@/lib/api/collection-data';
import type { CollectionWithDetails, CollectionColumn, Collection, ResourceOperation } from '@/types/entities';
import { Badge } from '@/components/ui/badge';
import { RecordEditorPage } from '@/components/shared/record-editor-page';
import { getAssetUrl, parseArrayValue } from '@/lib/collection-field-utils';

export type CollectionTenant = {
  id: number;
  slug: string;
  name: string;
  websiteUrl: string;
  collections: Collection[];
};

type CollectionDataPanelProps = {
  tenant: CollectionTenant;
  collection: CollectionWithDetails;
  onCollectionChange?: () => void;
  editorMode?: 'create' | 'edit' | null;
  editingItemId?: number | null;
  onCreateRoute?: () => void;
  onEditRoute?: (itemId: number) => void;
  onExitEditorRoute?: () => void;
  enforceAccess?: boolean;
};

interface DataItem {
  id: number;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

export function CollectionDataPanel({
  tenant,
  collection,
  onCollectionChange,
  editorMode = null,
  editingItemId = null,
  onCreateRoute,
  onEditRoute,
  onExitEditorRoute,
  enforceAccess = false,
}: CollectionDataPanelProps) {
  const [data, setData] = useState<DataItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DataItem | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [assetPathById, setAssetPathById] = useState<Record<number, string>>({});

  const allowedOps = useMemo(() => {
    const configuredOps = collection.details.ops?.length ? collection.details.ops : ALL_OPERATIONS;
    const normalizedOps = Array.from(new Set<ResourceOperation>(configuredOps));
    return ALL_OPERATIONS.filter((operation) => normalizedOps.includes(operation));
  }, [collection.details.ops]);

  const canCreate = !enforceAccess || allowedOps.includes('create');
  const canUpdate = !enforceAccess || allowedOps.includes('update');
  const canDelete = !enforceAccess || allowedOps.includes('delete');

  const editingItem = useMemo(() => {
    if (!editingItemId) {
      return undefined;
    }
    return data.find((item) => item.id === editingItemId);
  }, [data, editingItemId]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const items = await collectionDataApi.getAll(tenant.slug, collection.name);
      setData(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenant.slug, collection.name]);

  useEffect(() => {
    const fetchAssetPaths = async () => {
      try {
        const assets = await assetsApi.getAllByTenantSlug(tenant.slug);
        const nextAssetPathById = assets.reduce<Record<number, string>>((acc, asset) => {
          acc[asset.id] = asset.filepath;
          return acc;
        }, {});
        setAssetPathById(nextAssetPathById);
      } catch {
        setAssetPathById({});
      }
    };

    fetchAssetPaths();
  }, [tenant.slug]);

  const handleCreate = () => {
    if (!canCreate) {
      return;
    }
    onCreateRoute?.();
  };

  const handleEdit = (item: DataItem) => {
    if (!canUpdate) {
      return;
    }
    onEditRoute?.(item.id);
  };

  const handleDuplicate = async (item: DataItem) => {
    if (!canCreate) {
      return;
    }

    try {
      await collectionDataApi.duplicate(tenant.slug, collection.name, item.id);
      setError(null);
      await fetchData();
      onCollectionChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate data');
    }
  };

  const handleDelete = (item: DataItem) => {
    if (!canDelete) {
      return;
    }
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    setIsSaving(true);
    try {
      if (editorMode === 'edit' && editingItemId) {
        if (!canUpdate) {
          throw new Error('Update is disabled for this collection');
        }
        await collectionDataApi.update(tenant.slug, collection.name, editingItemId, formData);
      } else {
        if (!canCreate) {
          throw new Error('Create is disabled for this collection');
        }
        await collectionDataApi.create(tenant.slug, collection.name, formData);
      }
      setError(null);
      await fetchData();
      onCollectionChange?.();
      onExitEditorRoute?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    if (!canDelete) {
      setError('Delete is disabled for this collection');
      return;
    }

    try {
      await collectionDataApi.delete(tenant.slug, collection.name, itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(undefined);
      await fetchData();
      onCollectionChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete data');
    }
  };

  const formatCellValue = (value: unknown, column: CollectionColumn) => {
    if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;

    if (column.type === 'boolean') {
      return value ? (
        <Badge variant="default">Yes</Badge>
      ) : (
        <Badge variant="secondary">No</Badge>
      );
    }

    if (column.type === 'richtext') {
      const textContent = deltaToPlainText(value as string).replace(/\s+/g, ' ').trim();
      if (!textContent) {
        return <span className="text-muted-foreground">—</span>;
      }

      const previewText = textContent.length > 20 ? `${textContent.slice(0, 20)}...` : textContent;
      return (
        <span className="text-sm">
          {previewText}
        </span>
      );
    }

    if (column.type === 'image') {
      const assetId = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(assetId) || assetId <= 0) {
        return <span className="text-muted-foreground">—</span>;
      }

      const assetUrl = getAssetUrl(tenant.slug, assetId);
      const assetPath = assetPathById[assetId];

      return (
        <div className="flex min-w-0 items-center gap-2">
          <img
            src={assetUrl}
            alt={`Asset ${assetId}`}
            className="h-9 w-12 shrink-0 rounded border bg-muted/30 object-cover"
          />
          <span className="truncate font-mono text-xs text-muted-foreground">{assetPath ?? `Asset #${assetId}`}</span>
        </div>
      );
    }

    if (column.type === 'datetime') {
      return new Date(String(value)).toLocaleString();
    }

    if (column.type === 'array') {
      const rows = parseArrayValue(value);
      return (
        <span className={rows.length === 0 ? 'text-muted-foreground' : 'text-sm'}>{rows.length} {rows.length === 1 ? 'item' : 'items'}</span>
      );
    }

    return String(value);
  };

  const getSortableValue = (item: DataItem, column: CollectionColumn) => {
    const value = item[column.name];
    if (value === null || value === undefined) {
      return null;
    }

    if (column.type === 'datetime') {
      const date = new Date(String(value));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (column.type === 'image') {
      const assetId = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(assetId) || assetId <= 0) {
        return null;
      }
      return assetPathById[assetId] ?? '';
    }

    if (column.type === 'array') {
      return parseArrayValue(value).length;
    }

    if (column.type === 'number') {
      const numeric = Number(value);
      return Number.isNaN(numeric) ? null : numeric;
    }

    if (column.type === 'boolean') {
      return Boolean(value);
    }

    return String(value);
  };

  const columns: Column<DataItem>[] = [
    {
      key: 'id',
      header: 'ID',
      className: 'w-20',
      cell: (item) => <span className="text-muted-foreground">#{item.id}</span>,
      sortable: true,
      sortValue: (item) => item.id,
    },
    ...collection.details.columns.map<Column<DataItem>>((col) => ({
      key: col.name,
      header: col.label,
      cell: (item) => formatCellValue(item[col.name], col),
      sortable: true,
      sortValue: (item) => getSortableValue(item, col),
    })),
    {
      key: 'created_at',
      header: 'Created',
      className: 'w-32',
      cell: (item) => <span className="text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</span>,
      sortable: true,
      sortValue: (item) => new Date(item.created_at),
    },
    ...(canUpdate || canCreate || canDelete
      ? [{
          key: 'actions',
          header: 'Actions',
          className: 'w-28',
          cell: (item: DataItem) => (
            <div className="flex items-center gap-1">
              {canUpdate ? (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(item)} title="Edit">
                  <EditIcon className="size-3" />
                </Button>
              ) : null}
              {canCreate ? (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDuplicate(item)} title="Duplicate">
                  <CopyIcon className="size-3" />
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => handleDelete(item)}
                  title="Delete"
                >
                  <TrashIcon className="size-3" />
                </Button>
              ) : null}
            </div>
          ),
        }]
      : []),
  ];

  const searchKeys = useMemo(
    () => ['id', 'created_at', ...collection.details.columns.map((col) => col.name)] as (keyof DataItem)[],
    [collection.details.columns]
  );

  if (editorMode === 'edit' && !isLoading && !editingItem) {
    return (
      <div className="flex h-full flex-col">
        <div className="p-4 text-destructive text-sm">Record not found</div>
        <div className="px-4 pb-4">
          <Button variant="outline" onClick={() => onExitEditorRoute?.()}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (editorMode === 'create' && !canCreate) {
    return (
      <div className="flex h-full flex-col">
        <div className="p-4 text-destructive text-sm">Create is disabled for this collection</div>
        <div className="px-4 pb-4">
          <Button variant="outline" onClick={() => onExitEditorRoute?.()}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (editorMode === 'edit' && !canUpdate) {
    return (
      <div className="flex h-full flex-col">
        <div className="p-4 text-destructive text-sm">Update is disabled for this collection</div>
        <div className="px-4 pb-4">
          <Button variant="outline" onClick={() => onExitEditorRoute?.()}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (editorMode) {
    return (
      <RecordEditorPage
        key={`${editorMode}-${editingItemId ?? 'new'}-${collection.name}`}
        mode={editorMode}
        resourceName={collection.label || collection.name}
        columns={collection.details.columns}
        tenantSlug={tenant.slug}
        initialData={editorMode === 'edit' ? editingItem : undefined}
        onSave={handleSave}
        onBack={() => onExitEditorRoute?.()}
        isSaving={isSaving}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center gap-2">
        <Badge variant="secondary" className="font-mono">
          {collection.name}
        </Badge>
        <span className="text-sm text-muted-foreground">
          {data.length} {data.length === 1 ? 'record' : 'records'}
        </span>
      </div>

      {error && <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable
          data={data}
          columns={columns}
          searchKeys={searchKeys}
          searchPlaceholder="Search records..."
          emptyMessage="No data yet"
          isLoading={isLoading}
          onCreate={canCreate ? handleCreate : undefined}
          createLabel="Add Record"
          actions={
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
              <RefreshCwIcon className="size-4" />
              Refresh
            </Button>
          }
        />
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Record"
        description={`Are you sure you want to delete record #${itemToDelete?.id}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </div>
  );
}
